import axios from "axios";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import BlockUi from "react-block-ui";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";

import { customSelectStyle } from "../../../../_metronic/_partials/dropdowns/ReactSelectCustomStyle";
import { initialValues, subscriptionMigrationSchema } from "./schema";

export const ManageSubscriptionMigration = (props) => {
  const migrationId = props.match.params.id;
  const isEditing = migrationId !== undefined;

  const [isLoading, setIsLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansOptions, setPlansOptions] = useState([]);
  const history = useHistory();

  const {
    isSubmitting,
    errors,
    values,
    touched,
    setFieldValue,
    handleChange,
    handleSubmit,
  } = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: subscriptionMigrationSchema,
    onSubmit: async (values, { setSubmitting }) => onSubmitForm(values, setSubmitting),
  });

  const handleMapPlansForSelect = (plans) => {
    return plans.map((plan) => ({
      id: plan.gateway_id,
      label: plan.name,
    }));
  };

  const handleOnChangePlanAndPrice = useCallback((option, field, priceField) => {
    if (!option) {
      setFieldValue(field, null);
      setFieldValue(priceField, null);
      return;
    }

    const plan = plans.find(plan => plan.gateway_id === option.id);

    setFieldValue(field, { id: option.id, label: option.label });
    if (plan) {
      setFieldValue(priceField, plan.amount);
    } else {
      setFieldValue(priceField, null);
    }
  }, [plans, setFieldValue]);

  const handleFillMigrationData = useCallback((migration) => {
    const oldPlan = plans.find(plan => plan.gateway_id === migration.old_plan_id);
    const newPlan = plans.find(plan => plan.gateway_id === migration.new_plan_id);
    
    handleOnChangePlanAndPrice({
      id: oldPlan.gateway_id,
      label: oldPlan.name,
    }, "old_plan", "old_price");

    handleOnChangePlanAndPrice({
      id: newPlan.gateway_id,
      label: newPlan.name,
    }, "new_plan", "new_price");

    setFieldValue("after_days", migration.after_days);
  }, [handleOnChangePlanAndPrice, setFieldValue, plans]);

  useEffect(() => {
    const handleLoadActivePlans = async (name) => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/plans/active`, {
          params: {
            name
          }
        });
        setPlans(response.data.data);
        
        const allActivePlans = handleMapPlansForSelect(response.data.data);
        setPlansOptions(allActivePlans);
      } catch (error) {
        toast.error("Erro ao carregar os planos");
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    handleLoadActivePlans();
  }, []);

  useEffect(() => {
    const handleFindSubscriptionMigration = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/subscription/schedule-migration/find?id=${migrationId}`);

        if (!response.data.success) {
          toast.error("Houve um erro inesperado ao carregar a migração.");
          history.push("/subscription_migration");
        }
        handleFillMigrationData(response.data.data);
      } catch (error) {
        toast.error("Tivemos um problema ao carregar a migração.");
        console.error(error);
      }
      finally {
        setIsLoading(false);
      }
    };
    if (isEditing && plans.length) {
      handleFindSubscriptionMigration(migrationId);
    }
  }, [isEditing, migrationId, handleFillMigrationData, history, plans]);

  const onSubmitForm = async (values, setSubmitting) => {
    try {
      setIsLoading(true);

      if (!isEditing) {
        const alreadyScheduledMigration = await checkExistingMigration(values.old_plan.id);
        
        if (alreadyScheduledMigration) {
          toast.error("Já existe uma migração pendente para o plano atual selecionado");
          return;
        }
      }

      const response = isEditing ?
        await updateSubscriptionMigration(values) :
        await createSubscriptionMigration(values);
      
      if (response.success) {
        toast.success("As alterações foram salvas com sucesso");
        history.push("/subscription_migration");
      }
    } catch (error) {
      toast.error("Tivemos um problema ao salvar as alterações realizadas");
      console.error(error);
    } finally {
      setSubmitting(false);
      setIsLoading(false);
    }
  };

  const checkExistingMigration = async (oldPlanId) => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/subscription/check-subscription-migration-pending?gateway_id=${oldPlanId}`);
    const isAlreadyScheduledWithOldPlan = response.data.data.scheduled;  
    return isAlreadyScheduledWithOldPlan;
  };

  const createSubscriptionMigration = async (data) => {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/subscription/schedule-migration/create`, {
      migration: {
        ...data,
        new_plan: data.new_plan.id,
        old_plan: data.old_plan.id
      }
    });
    return response.data;
  };

  const updateSubscriptionMigration = async (data) => {
    const response = await axios.put(`${process.env.REACT_APP_API_URL}/subscription/schedule-migration/edit`, {
      migration: {
        ...data,
        id: Number.parseInt(migrationId),
        new_plan: data.new_plan.id,
        old_plan: data.old_plan.id
      }
    });
    return response.data;
  };

  return (
    <BlockUi tag="div" blocking={isSubmitting || isLoading}>
      <div className="row no-gutters mb-20">
        <div className="col-xl-12">
          <div
            className="border-radius-only-left"
            style={{ backgroundColor: "#fff", padding: "40px", height: "100%" }}
          >
            <div className="row mb-5">
              <div className="col-xl-6">
                <label className="form-label">
                  Dias para a migração <span className="text-danger">&nbsp;*</span>
                </label>
                <input
                  type="number"
                  name="after_days"
                  aria-label="Dias para a migração"
                  className={
                    "form-control " +
                    (errors.after_days && touched.after_days
                      ? "is-invalid"
                      : "")
                  }
                  value={values.after_days}
                  onChange={handleChange}
                />
                {errors.after_days && touched.after_days && (
                  <label className="invalid-feedback">
                    {errors.after_days}
                  </label>
                )}
              </div>
            </div>
            <div className="row mb-5">
              <div className="col-xl-6">
                <label className="form-label">
                  Plano Atual <span className="text-danger">&nbsp;*</span>
                </label>
                <Select
                  name="old_plan"
                  isSearchable={true}
                  isClearable={true}
                  options={plansOptions}
                  className={
                    errors.old_plan && touched.old_plan
                      ? "is-invalid"
                      : ""
                  }
                  onChange={option => handleOnChangePlanAndPrice(option, "old_plan", "old_price")}
                  value={values.old_plan}
                  placeholder="Selecione.."
                  getOptionLabel={option => option.label}
                  getOptionValue={option => option.id}
                  noOptionsMessage={() => <span>Sem resultados.</span>}
                  theme={customSelectStyle}
                />
                {errors.old_plan && touched.old_plan && (
                  <label className="invalid-feedback">
                    {errors.old_plan}
                  </label>
                )}
              </div>
              <div className="col-xl-6">
                <label className="form-label">
                  Novo Plano <span className="text-danger">&nbsp;*</span>
                </label>
                <Select
                  name="new_plan"
                  isSearchable={true}
                  isClearable={true}
                  options={plansOptions}
                  className={
                    errors.new_plan && touched.new_plan
                      ? "is-invalid"
                      : ""
                  }
                  onChange={option => handleOnChangePlanAndPrice(option, "new_plan", "new_price")}
                  value={values.new_plan}
                  placeholder="Selecione.."
                  getOptionLabel={option => option.label}
                  getOptionValue={option => option.id}
                  noOptionsMessage={() => <span>Sem resultados.</span>}
                  theme={customSelectStyle}
                />
                {errors.new_plan && touched.new_plan && (
                  <label className="invalid-feedback">
                    {errors.new_plan}
                  </label>
                )}
              </div>
            </div>
            <div className="row mb-5">
              <div className="col-xl-12">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn btn-primary"
                  style={{ width: "100%", height: "50px" }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "ATUALIZANDO..." : isEditing ? "EDITAR MIGRAÇÃO" : "CRIAR MIGRAÇÃO"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BlockUi>
  );
};
